import { useState } from 'react';

import { ownersErrorMessage, removeOwner } from '../../lib/boatOwners';

// Inline two-step arm → fire block for leaving a boat that has other owners
// (TOM-458, ADR 0319), in place of Delete: only the last owner deletes a boat.
// Same shape as DeleteBoatConfirm. Leaving is DELETE
// /v1/boats/:boatId/owners/<your own id>, with an Idempotency-Key.

interface LeaveBoatConfirmProps {
  boatId: string;
  boatName: string;
  userId: string;
  otherOwners: number;
  /** Called after the server has accepted the leave. */
  onLeft: () => void;
}

export function LeaveBoatConfirm({
  boatId,
  boatName,
  userId,
  otherOwners,
  onLeft,
}: LeaveBoatConfirmProps): React.ReactElement {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function doLeave(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await removeOwner(boatId, userId);
      onLeft();
    } catch (err) {
      setError(ownersErrorMessage(err, 'Could not leave this boat. Try again.'));
      setBusy(false);
    }
  }

  const others = otherOwners === 1 ? 'the other owner' : `the other ${otherOwners} owners`;

  if (!armed) {
    return (
      <button
        type="button"
        className="btn btn-danger"
        data-testid="boat-leave-btn"
        onClick={() => {
          setError(null);
          setArmed(true);
        }}
      >
        Leave this boat
      </button>
    );
  }

  return (
    <div className="alarm-delete-confirm" data-testid="boat-leave-confirm">
      <p>
        Leave <strong>{boatName}</strong>? You will no longer see it or get its alarms. It stays
        with {others}, and one of them can invite you back.
      </p>
      <div className="alarm-form-actions">
        <button
          type="button"
          className="btn btn-danger"
          disabled={busy}
          data-testid="boat-leave-confirm-btn"
          onClick={() => void doLeave()}
        >
          {busy ? 'Leaving…' : 'Leave boat'}
        </button>
        <button type="button" className="btn" disabled={busy} onClick={() => setArmed(false)}>
          Stay
        </button>
      </div>
      {error !== null ? (
        <p className="alarm-form-err" role="alert" data-testid="boat-leave-error">
          {error}
        </p>
      ) : null}
    </div>
  );
}
